/**
 * PushNotificationsCard Component
 *
 * Browser push notifications card for settings page.
 * Displays toggle to enable/disable browser push notifications.
 *
 * Design Compliance:
 * - Single Card pattern (matches other settings cards)
 * - Icon + Title header with toggle in header
 * - Uses Polaris spacing scale (gap="400")
 * - Toggle with custom switch styling
 */

import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Icon,
} from '@shopify/polaris';
import { NotificationIcon } from '@shopify/polaris-icons';
import { Toggle } from '../../ui';

export interface PushNotificationsCardProps {
  /** Whether browser notifications are enabled */
  enabled: boolean;
  /** Handler for toggling browser notifications */
  onToggle: (enabled: boolean) => void;
  /** Whether the toggle is disabled */
  isLoading?: boolean;
}

export function PushNotificationsCard({
  enabled,
  onToggle,
  isLoading = false,
}: PushNotificationsCardProps) {
  return (
    <Card>
      <InlineStack align="space-between" blockAlign="center" wrap={false}>
        {/* Header with icon and text */}
        <InlineStack gap="200" blockAlign="center">
          <Box>
            <Icon source={NotificationIcon} tone="subdued" />
          </Box>
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              Push notifications
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Receive alerts directly in your browser
            </Text>
          </BlockStack>
        </InlineStack>

        {/* Toggle in header */}
        <Toggle
          label="Enable push notifications"
          enabled={enabled}
          onChange={onToggle}
          disabled={isLoading}
        />
      </InlineStack>
    </Card>
  );
}
